import { assertProfileAvailable } from '../../Common/profileAvailability';
import { Autocomplete, TextField } from '@mui/material';
import { get, ref, update } from 'firebase/database';
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getTeachers, updateStatisticsFunc } from '../../Common/Database';
import { database } from '../../Common/Firebase';

import '../ModalMaterial/style.scss';
import '../CreateStudentModal/style.scss';
import { throwErrorRollbar } from '../../index';
import { departmentsArr } from '../CreateStudentModal/utils';

const CreateTeacherModal = ({ open, setOpen, submit, fetchTeachersListData, title, teacher, editTeacher }) => {
  const { t } = useTranslation();
  const [lastName, setLastName] = useState(teacher ? teacher.lastName : '');
  const [firstName, setFirstName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [email, setEmail] = useState(teacher ? teacher.email : '');
  const [branch, setBranch] = useState('');
  const [lastNameDirty, setLastNameDirty] = useState(false);
  const [lastNameError, setLastNameError] = useState('');
  const [firstNameDirty, setFirstNameDirty] = useState(false);
  const [firstNameError, setFirstNameError] = useState('');
  const [patronymicDirty, setPatronymicDirty] = useState(false);
  const [patronymicError, setPatronymicError] = useState('');
  const [emailDirty, setEmailDirty] = useState(false);
  const [emailError, setEmailError] = useState(t('field is required'));
  const [phoneDirty, setPhoneDirty] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [cityDirty, setCityDirty] = useState(false);
  const [cityError, setCityError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [unicTeacher, setUnicTeacher] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const autocompleteRef = useRef(null);

  const lastNameHandler = e => {
    const value = e.target.value;
    setLastNameDirty(true);

    if (value === undefined || value.trim() === '') {
      setLastNameError(t('field is required'));
    } else if (!/^(?!.*['`]{2})(?!.*['`].*['`])([A-Z][a-z]*|[А-ЯЁЇІЄҐ][а-яёїієґ']*)$/.test(value.trim())) {
      setLastNameError(t('Last name must start with a capital letter and contain only letters'));
    } else {
      setLastNameError('');
    }
  };

  const firstNameHandler = e => {
    const value = e.target.value;
    setFirstNameDirty(true);

    if (value === undefined || value.trim() === '') {
      setFirstNameError(t(''));
    } else if (!/^(?!.*['`]{2})(?!.*['`].*['`])([A-Z][a-z]*|[А-ЯЁЇІЄҐ][а-яёїієґ']*)$/.test(value.trim())) {
      setFirstNameError(t('First name must start with a capital letter and contain only letters'));
    } else {
      setFirstNameError('');
    }
  };

  const patronymicHandler = e => {
    const value = e.target.value;
    setPatronymicDirty(true);

    if (value === undefined || value.trim() === '') {
      setPatronymicError(t(''));
    } else if (!/^(?!.*['`]{2})(?!.*['`].*['`])([A-Z][a-z]*|[А-ЯЁЇІЄҐ][а-яёїієґ']*)$/.test(value.trim())) {
      setPatronymicError(t('Patronymic must start with a capital letter and contain only letters'));
    } else {
      setPatronymicError('');
    }
  };

  const emailHandler = e => {
    const value = e.target.value;
    const re =
      /^(([a-z0-9]{1}[-0-9a-z\.]{1,}[0-9a-z]{1})|([0-9]{1}[-0-9a-z\.]{1,}[0-9]{1}))@([-0-9a-z]{1,}\.){1,2}[-a-z]{2,}$/u;

    setEmail(value);

    if (value === undefined || value.trim() === '') {
      setEmailError(t('field is required'));
    } else if (!re.test(String(value).toLocaleLowerCase())) {
      setEmailError(t('Wrong email address format'));
    } else {
      setEmailError('');
    }
  };

  const phoneHandler = e => {
    const value = e.target.value;
    setPhoneDirty(true);

    if (value.trim() === '') {
      setPhoneError('');
    } else if (!/^\+380\d{9}$/.test(value.trim())) {
      setPhoneError(t('Phone number can only be a number'));
    } else {
      setPhoneError('');
    }
  };

  const cityHandler = e => {
    const value = e.target.value;
    setCityDirty(true);

    if (value === undefined || value.trim() === '') {
      setCityError(t(''));
    } else if (
      !/^(?!.*['`]{2})(?!.*['`].*['`])(?!.*[-`]{2})(?!.*[-`].*[-`])(?!.*[ `]{2})(?!.*[ `].*[ `])([A-Z][a-z]*|[А-ЯЁЇІЄҐ][а-яёїієґ'\s-]*)$/.test(
        value.trim(),
      )
    ) {
      setCityError(t('The name of the city must consist only of letters and begin with a capital letter'));
    } else {
      setCityError('');
    }
  };

  const blurHandler = e => {
    const condition = e.target.value === undefined || e.target.value.trim() === '';

    switch (e.target.name) {
      case 'lastName':
        setLastNameDirty(true);
        if (condition) {
          setLastNameError(t('field is required'));
        } else {
          setLastNameError('');
        }
        break;

      case 'firstName':
        setFirstNameDirty(true);
        if (condition) {
          setFirstNameError(t('field is required'));
        } else {
          setFirstNameError('');
        }
        break;

      case 'email':
        setEmailDirty(true);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if ((lastNameError || firstNameError || patronymicError || emailError || phoneError || cityError) && !editTeacher) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  }, [lastNameError, firstNameError, patronymicError, emailError, phoneError, cityError, unicTeacher]);

  const nameInitials = (lastName, firstName, patronymic) => {
    let initials = `${lastName.charAt(0).toUpperCase()}${lastName.slice(1)} ${firstName.charAt(0).toUpperCase()}.`;

    if (patronymic) {
      initials += ` ${patronymic.charAt(0).toUpperCase()}.`;
    }

    return initials;
  };

  const onSubmitFunc = async () => {
    let profileAddress;
    try {
      profileAddress = await assertProfileAvailable(email, 'Teachers', editTeacher ? teacher?.email?.replace(/\./g, '') : undefined,
        path => get(ref(database, path)));
    } catch (error) {
      setUnicTeacher(error.message || 'Не вдалося перевірити профілі. Спробуйте ще раз.');
      return;
    }
    const normalizedEmail = profileAddress.email;
    const id = normalizedEmail.replace(/\./g, '');
    const initials = nameInitials(lastName, firstName, patronymic);
    const teachersList = await getTeachers();
    let mapList = Object.values(teachersList);
    const unicEmail = mapList.filter(item => item?.email?.toLowerCase() === normalizedEmail);
    if (unicEmail.length > 0 && !editTeacher) {
      setUnicTeacher(t('teacher with this email address already exists'));
    } else {
      try {
        await update(ref(database, `Teachers/${id}`), {
          FIO: initials,
          lastName: lastName,
          firstName: firstName,
          patronymic: patronymic,
          phone: phone,
          city: city,
          email: normalizedEmail,
          branch: branch,
          active: true,
          type: 'Teacher',
          subjects: {
            english: {
              activeSub: false,
              name: 'english',
              getNotebook: false,
            },
            geography: {
              activeSub: false,
              name: 'geography',
              getNotebook: false,
            },
            geometry: {
              activeSub: false,
              name: 'geometry',
              getNotebook: false,
            },
            historyUkraine: {
              activeSub: false,
              name: 'historyUkraine',
              getNotebook: false,
            },
            ukrainianLanguage: {
              activeSub: false,
              name: 'ukrainianLanguage',
              getNotebook: false,
            },
            physics: {
              activeSub: false,
              name: 'physics',
              getNotebook: false,
            },
            biology: {
              activeSub: false,
              name: 'biology',
              getNotebook: false,
            },
            mathematics: {
              activeSub: false,
              name: 'mathematics',
              getNotebook: false,
            },
            nature: {
              activeSub: false,
              name: 'nature',
              getNotebook: false,
            },
            algebra: {
              activeSub: false,
              name: 'algebra',
              getNotebook: false,
            },
            youngSchoolMathematics: {
              activeSub: false,
              name: 'youngSchoolMathematics',
              getNotebook: false,
            },
            allWorldHistory: {
              activeSub: false,
              name: 'allWorldHistory',
              getNotebook: false,
            },
            chemistry: {
              activeSub: false,
              name: 'chemistry',
              getNotebook: false,
            },
            youngSchoolRussianLanguage: {
              activeSub: false,
              name: 'youngSchoolRussianLanguage',
              getNotebook: false,
            },
            ukrainianLiterature: {
              activeSub: false,
              name: 'ukrainianLiterature',
              getNotebook: false,
            },
            foreignLiterature: {
              activeSub: false,
              name: 'foreignLiterature',
              getNotebook: false,
            },
            youngUkrainianLanguage: {
              activeSub: false,
              name: 'youngUkrainianLanguage',
              getNotebook: false,
            },
            german: {
              activeSub: false,
              name: 'german',
              getNotebook: false,
            },
            kvol: {
              activeSub: false,
              name: 'kvol',
              getNotebook: false,
            },
          },
        });
        updateStatisticsFunc('FindingTeachersSite', 'increment');
        setOpen(false);
        toast.success(t('popupTeacherAdded'));
        fetchTeachersListData();
      } catch (error) {
        console.log(error);
        throwErrorRollbar(error);
      }
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter' || event.key === 'Done') {
      if (autocompleteRef.current) {
        autocompleteRef.current.blur();
      }
    }
  };

  return (
    <div
      className={open ? 'modal-form modal-form--open' : 'modal-form'}
      onClick={e => {
        if (!isFocused) {
          setOpen(false);
        }
        e.stopPropagation();
      }}
    >
      <div
        className="modal-form-content"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <form>
          <div className="modal-header">
            <h2>{title}:</h2>
            <button
              className="btn btn-icon"
              onClick={e => {
                setOpen(false);
                e.preventDefault();
              }}
            >
              <span className="icon icon-plus"></span>
            </button>
          </div>

          {unicTeacher && <span className="allert-error">{unicTeacher}</span>}

          <div className="modal-body" id="modal-body">
            <div className="form-control autocomplete-control">
              <Autocomplete
                id="lastName"
                options={[]}
                value={teacher ? teacher.lastName : ''}
                onInputChange={(e, v) => setLastName(v)}
                onBlur={blurHandler}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('lastName')}
                    value={teacher.lastName}
                    onChange={lastNameHandler}
                    name="lastName"
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {lastNameDirty && lastNameError && <span className="error">{lastNameError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                id="firstName"
                options={[]}
                value={teacher ? teacher.firstName : ''}
                onInputChange={(e, v) => setFirstName(v)}
                onBlur={blurHandler}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('firstName')}
                    value={teacher.firstName}
                    onChange={firstNameHandler}
                    name="firstName"
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {firstNameDirty && firstNameError && <span className="error">{firstNameError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                id="patronymic"
                options={[]}
                value={teacher ? teacher.patronymic : ''}
                onInputChange={(e, v) => setPatronymic(v)}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('patronymic')}
                    value={teacher.patronymic}
                    onChange={patronymicHandler}
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {patronymicDirty && patronymicError && <span className="error">{patronymicError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                id="email"
                options={[]}
                value={teacher ? teacher.email : ''}
                onBlur={blurHandler}
                readOnly={!!teacher.email}
                disabled={!!teacher.email}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('email')}
                    value={teacher.email}
                    onChange={emailHandler}
                    name="email"
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {emailDirty && emailError && <span className="error">{emailError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                id="phone"
                options={[]}
                value={teacher ? teacher.phone : ''}
                onInputChange={(e, v) => setPhone(v)}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('phoneNumber')}
                    value={teacher.phone}
                    onChange={phoneHandler}
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {phoneDirty && phoneError && <span className="error">{phoneError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                id="city"
                options={[]}
                value={teacher ? teacher.city : ''}
                onInputChange={(e, v) => setCity(v)}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('city')}
                    value={teacher.city}
                    onChange={cityHandler}
                    name="city"
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {cityDirty && cityError && <span className="error">{cityError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                id="branch"
                options={departmentsArr}
                value={branch}
                onInputChange={(e, v) => setBranch(v)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('branch')}
                    value={teacher.branch}
                    placeholder=" "
                    onKeyPress={handleKeyPress}
                    inputRef={autocompleteRef}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-primary more btn-active"
              onClick={e => {
                onSubmitFunc();
                e.preventDefault();
              }}
              disabled={!isFormValid}
            >
              {submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeacherModal;
